/**
 * All display formatting lives here, so a timestamp or a hash reads the same
 * on every screen.
 */

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

type DateInput = string | number | Date | null | undefined;

function toDate(value: DateInput): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "just now", "5 min ago", "3 h ago", "yesterday", "12 days ago", or a date; future: "in 5 min". */
export function relativeTime(value: DateInput, now: number = Date.now()): string {
  const d = toDate(value);
  if (!d) return '—';
  const diff = now - d.getTime();
  const abs = Math.abs(diff);
  const future = diff < 0;
  const fmt = (n: number, unit: string) => (future ? `in ${n} ${unit}` : `${n} ${unit} ago`);

  if (abs < 45 * SECOND) return future ? 'in a moment' : 'just now';
  if (abs < 90 * SECOND) return fmt(1, 'min');
  if (abs < 55 * MINUTE) return fmt(Math.round(abs / MINUTE), 'min');
  if (abs < 22 * HOUR) return fmt(Math.max(1, Math.round(abs / HOUR)), 'h');
  if (abs < 36 * HOUR) return future ? 'tomorrow' : 'yesterday';
  if (abs < 30 * DAY) return fmt(Math.round(abs / DAY), 'days');
  return shortDate(d);
}

/** "Sep 21, 2026" */
export function shortDate(value: DateInput): string {
  const d = toDate(value);
  if (!d) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** "Sep 21, 2026, 14:03:22" in the viewer's time zone. */
export function dateTime(value: DateInput, opts: { seconds?: boolean } = {}): string {
  const d = toDate(value);
  if (!d) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...(opts.seconds !== false && { second: '2-digit' }),
    hour12: false,
  });
}

/** "14:03" */
export function clockTime(value: DateInput): string {
  const d = toDate(value);
  if (!d) return '—';
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
}

/** Full ISO timestamp for title attributes. */
export function isoTitle(value: DateInput): string {
  const d = toDate(value);
  return d ? d.toISOString() : '';
}

/** A window given in unix seconds: "14:02:00 – 14:03:00, Sep 21". */
export function unixWindow(start: number, end: number): string {
  const s = new Date(start * 1000);
  const e = new Date(end * 1000);
  const t = (d: Date) => d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  const day = s.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return `${t(s)} – ${t(e)}, ${day}`;
}

/** Duration in seconds, compact: "45 s", "5 min", "2 h", "3 d". */
export function duration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '—';
  if (seconds < 60) return `${Math.round(seconds)} s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)} min`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)} h`;
  return `${Math.round(seconds / 86400)} d`;
}

/** 1536 -> "1.5 KB". Binary multiples, one decimal under 10. */
export function bytes(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n) || n < 0) return '—';
  if (n < 1024) return `${n} B`;
  const units = ['KB', 'MB', 'GB', 'TB', 'PB'];
  let v = n;
  let i = -1;
  do {
    v /= 1024;
    i++;
  } while (v >= 1024 && i < units.length - 1);
  return `${v < 10 ? v.toFixed(1) : Math.round(v)} ${units[i]}`;
}

/** 12345 -> "12,345"; 1234567 with compact -> "1.2M". */
export function count(n: number | null | undefined, opts: { compact?: boolean } = {}): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  return new Intl.NumberFormat(undefined, opts.compact ? { notation: 'compact', maximumFractionDigits: 1 } : {}).format(n);
}

/** "3 agents", "1 agent". */
export function plural(n: number, one: string, many = `${one}s`): string {
  return `${count(n)} ${n === 1 ? one : many}`;
}

/**
 * Middle-truncate a long value, keeping both ends: "a1b2c3…x7y8z9".
 * `head`/`tail` count characters kept on each side.
 */
export function truncateMiddle(value: string, head = 8, tail = 6): string {
  if (!value) return '';
  if (value.length <= head + tail + 1) return value;
  return `${value.slice(0, head)}…${value.slice(-tail)}`;
}

/** A person as the UI names them: display name, else a shortened wallet address. */
export function personName(p: { display_name?: string | null; displayName?: string | null; wallet_address?: string; walletAddress?: string }): string {
  const name = p.display_name ?? p.displayName;
  if (name) return name;
  const addr = p.wallet_address ?? p.walletAddress ?? '';
  return addr ? truncateMiddle(addr, 6, 4) : 'Unknown';
}

/** Initials for an avatar: "Ada Lovelace" -> "AL", "1Abc…" -> "1A". */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

/** "file_changed" -> "File changed". */
export function humanize(token: string): string {
  const s = token.replace(/[_-]+/g, ' ').trim().toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}
